import { BulkUploadTypes, UploadTypeConfig } from 'constants/bulkUpload';
import { useEffect, useMemo, useState } from 'react';
import { ColumnSelectorProps } from '../BladeComponents/ColumnsSelector';
import { useBulkUploadTypeConfig } from './useBulkUploadTypeConfig';

type UseColumnAndTabsSelectorDataReturnType = {
  tabsData: {
    showTabs: boolean;
    tabs: {
      title: string;
      value: string;
    }[];
    value: string;
    onTabChange: React.Dispatch<React.SetStateAction<string>>;
  };
  columnsData: ColumnSelectorProps & {
    renderColumns: NonNullable<UploadTypeConfig['preview']['columns']>;
  };
  sheetNumber?: number;
};

export const useColumnsAndTabsSelectorData = ({
  bulkUploadType,
}: {
  bulkUploadType: BulkUploadTypes;
}): UseColumnAndTabsSelectorDataReturnType => {
  const uploadTypeConfig = useBulkUploadTypeConfig(bulkUploadType);
  const previewConfig = uploadTypeConfig.preview;

  const sheetsData = previewConfig.sheets;
  const sheetTitles: Array<string> = sheetsData ? Object.keys(sheetsData) : [];

  const [visibleTab, setVisibleTab] = useState(sheetTitles[0] || '');

  const showTabs = sheetTitles.length > 0;

  const allColumns = showTabs && sheetsData ? sheetsData[visibleTab].columns : previewConfig.columns ?? [];

  const initialVisibleColumnTitles = useMemo(() => {
    return [...allColumns.filter((_, idx) => idx < 5).map(({ title }) => title)];
  }, [visibleTab, showTabs]);

  const [visibleColumnTitles, setVisibleColumnTitles] = useState(initialVisibleColumnTitles);

  useEffect(() => {
    setVisibleColumnTitles(initialVisibleColumnTitles);
  }, [initialVisibleColumnTitles]);

  const renderColumns = useMemo(() => {
    return visibleColumnTitles.length && showTabs
      ? allColumns.filter(({ title }) => visibleColumnTitles.includes(title))
      : allColumns;
  }, [showTabs, visibleColumnTitles, visibleTab]);

  return {
    sheetNumber: showTabs ? sheetTitles.indexOf(visibleTab) : undefined,
    tabsData: {
      showTabs,
      tabs: sheetTitles.map((title) => ({
        title,
        value: title,
      })),
      value: visibleTab,
      onTabChange: setVisibleTab,
    },
    columnsData: {
      options: allColumns.map(({ title }) => ({
        title,
        value: title,
      })),
      onChange: setVisibleColumnTitles,
      values: visibleColumnTitles,
      renderColumns,
    },
  };
};
